import { useCallback, useEffect, useState } from "react";
import { ArrowDown, ArrowUp, ChevronLeft, ChevronRight, Download, Search, SearchX } from "lucide-react";
import { toast } from "sonner";
import api, { download, errMsg } from "@/lib/api";
import { useApp } from "@/context/AppContext";
import { useAuth } from "@/context/AuthContext";
import { fmtDate, toParams } from "@/lib/constants";
import { FilterBar } from "@/components/FilterBar";
import { BrandTag, EmptyState, LoadingBlock, PageHeader, SeverityChip, StatusChip } from "@/components/common";
import { RecordPanel } from "@/components/RecordPanel";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const COLS = [["date", "Date"], ["brand", "Brand"], ["outlet", "Outlet"], ["platform", "Platform"], ["customer_name", "Customer"], ["rating", "Rating"], ["text", "Feedback"], ["requested_refund", "Requested refund"], ["refund_amount", "Refund amount"], ["category", "Category"], ["department", "Team"], ["severity", "Severity"], ["status", "Status"]];

const money = (v) => (v == null || v === "" ? "—" : `₹${Number(v).toLocaleString("en-IN")}`);

const BulkBar = ({ count, statuses, onApply, onClear }) => {
  const [st, setSt] = useState("");
  return (
    <div data-testid="bulk-bar" className="mb-4 flex flex-wrap items-center gap-3 rounded-2xl bg-slate-900 px-4 py-3 text-white fade-up">
      <span className="font-semibold">{count} selected</span>
      <Select value={st} onValueChange={setSt}>
        <SelectTrigger data-testid="bulk-status-select" className="h-10 w-[180px] rounded-xl border-slate-700 bg-slate-800 text-white"><SelectValue placeholder="Change status to..." /></SelectTrigger>
        <SelectContent>{statuses.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
      </Select>
      <Button data-testid="bulk-apply-button" disabled={!st} onClick={() => onApply(st)} className="h-10 rounded-xl bg-white text-slate-900 hover:bg-slate-100">Apply</Button>
      <button onClick={onClear} className="ml-auto text-sm text-slate-300 hover:text-white" data-testid="bulk-clear">Clear selection</button>
    </div>
  );
};

export default function Complaints() {
  const { meta, filters, setFilters, complaintSentiment, setComplaintSentiment } = useApp();
  const { can } = useAuth();
  const [data, setData] = useState(null);
  const [page, setPage] = useState(1);
  const [sort, setSort] = useState(["date", "desc"]);
  const [sel, setSel] = useState([]);
  const [openId, setOpenId] = useState(null);
  const [q, setQ] = useState(filters.q);
  const params = toParams({ ...filters, sentiment: complaintSentiment });
  const key = JSON.stringify(params);

  useEffect(() => {
    const t = setTimeout(() => setFilters((f) => ({ ...f, q })), 350);
    return () => clearTimeout(t);
  }, [q, setFilters]);
  useEffect(() => {
    setPage(1);
  }, [key]);

  const load = useCallback(() => {
    api.get("/records", { params: { ...JSON.parse(key), page, page_size: 25, sort_by: sort[0], sort_dir: sort[1] } })
      .then((r) => setData(r.data)).catch((e) => toast.error(errMsg(e)));
  }, [key, page, sort]);
  useEffect(() => {
    load();
  }, [load]);

  const toggleSort = (c) => setSort(([f, d]) => [c, f === c && d === "desc" ? "asc" : "desc"]);
  const bulk = async (status) => {
    try {
      const { data: r } = await api.post("/records/bulk-status", { ids: sel, status });
      toast.success(`${r.updated} records set to ${status}${r.skipped ? ` · ${r.skipped} skipped (finish their Follow-up calls first)` : ""}`);
      setSel([]);
      load();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  const exp = (fmt) => download("/records/export", { ...params, format: fmt }, `complaints.${fmt}`).catch((e) => toast.error(errMsg(e)));
  const items = data?.items || [];
  const pages = data ? Math.max(1, Math.ceil(data.total / data.page_size)) : 1;
  const allSel = items.length > 0 && items.every((r) => sel.includes(r.id));

  return (
    <div data-testid="complaints-page">
      <PageHeader title="Complaints" subtitle="All feedback and complaints in one list. Click a row to see details and update it.">
        <Button variant="outline" className="h-11 rounded-xl" onClick={() => exp("xlsx")} data-testid="export-excel-button"><Download className="mr-2 h-4 w-4" />Excel</Button>
        <Button variant="outline" className="h-11 rounded-xl" onClick={() => exp("csv")} data-testid="export-csv-button"><Download className="mr-2 h-4 w-4" />CSV</Button>
      </PageHeader>
      <div className="relative mb-3">
        <Search className="absolute left-4 top-1/2 h-5 w-5 -translate-y-1/2 text-slate-400" />
        <Input data-testid="complaints-search-input" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search by customer, phone, order ID, words in the feedback..." className="h-12 rounded-2xl border-slate-200 bg-white pl-12 text-base" />
      </div>
      <FilterBar sentiment={complaintSentiment} onSentiment={setComplaintSentiment} onClear={() => setComplaintSentiment(["Negative"])} />
      {sel.length > 0 && can("admin", "manager") && meta && <BulkBar count={sel.length} statuses={meta.statuses} onApply={bulk} onClear={() => setSel([])} />}
      {!data ? <LoadingBlock rows={8} /> : !items.length ? (
        <EmptyState icon={SearchX} title="No complaints match" text="Try removing a filter or changing the date range." testid="complaints-empty" />
      ) : (
        <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white">
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="complaints-table">
              <thead className="bg-slate-50">
                <tr className="text-left text-xs font-semibold uppercase tracking-wider text-slate-500">
                  {can("admin", "manager") && <th className="w-10 px-4 py-3"><Checkbox data-testid="select-all-checkbox" checked={allSel} onCheckedChange={(v) => setSel(v ? items.map((r) => r.id) : [])} /></th>}
                  {COLS.map(([c, l]) => (
                    <th key={c} className="whitespace-nowrap px-3 py-3">
                      <button onClick={() => toggleSort(c)} className="inline-flex items-center gap-1 hover:text-slate-900" data-testid={`sort-${c}`}>
                        {l}{sort[0] === c && (sort[1] === "desc" ? <ArrowDown className="h-3 w-3" /> : <ArrowUp className="h-3 w-3" />)}
                      </button>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {items.map((r) => (
                  <tr key={r.id} data-testid={`complaint-row-${r.id}`} onClick={() => setOpenId(r.id)} className="cursor-pointer border-t border-slate-100 transition-colors hover:bg-slate-50">
                    {can("admin", "manager") && (
                      <td className="px-4 py-3" onClick={(e) => e.stopPropagation()}>
                        <Checkbox data-testid={`select-row-${r.id}`} checked={sel.includes(r.id)} onCheckedChange={(v) => setSel((s) => (v ? [...s, r.id] : s.filter((x) => x !== r.id)))} />
                      </td>
                    )}
                    <td className="whitespace-nowrap px-3 py-3 text-slate-600">{fmtDate(r.date)}</td>
                    <td className="px-3 py-3"><BrandTag brand={r.brand} /></td>
                    <td className="whitespace-nowrap px-3 py-3 font-medium text-slate-800">{r.outlet}</td>
                    <td className="px-3 py-3 text-slate-600">{r.platform}</td>
                    <td className="whitespace-nowrap px-3 py-3 text-slate-700">{r.customer_name || "—"}</td>
                    <td className="whitespace-nowrap px-3 py-3 text-slate-700" data-testid={`rating-${r.id}`}>{r.rating ?? "—"}</td>
                    <td className="min-w-[260px] max-w-[340px] px-3 py-3 text-slate-600"><div className="line-clamp-2">{r.text || "—"}</div></td>
                    <td className="whitespace-nowrap px-3 py-3 text-slate-700">{money(r.requested_refund)}</td>
                    <td className="whitespace-nowrap px-3 py-3 text-slate-700">{money(r.refund_amount)}</td>
                    <td className="whitespace-nowrap px-3 py-3 text-slate-700">{r.category}</td>
                    <td className="whitespace-nowrap px-3 py-3" data-testid={`dept-${r.id}`}>{r.department ? <span className={`rounded-full px-2 py-0.5 text-xs font-semibold ${r.department === "BOH" ? "bg-orange-50 text-orange-700" : "bg-sky-50 text-sky-700"}`}>{r.department === "BOH" ? "BOH · Kitchen" : "FOH · Service"}</span> : "—"}</td>
                    <td className="px-3 py-3"><SeverityChip severity={r.severity} /></td>
                    <td className="px-3 py-3"><StatusChip status={r.status} testid={`status-chip-${r.id}`} /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="flex items-center justify-between border-t border-slate-100 px-4 py-3 text-sm text-slate-500">
            <span data-testid="complaints-total">{data.total} records</span>
            <div className="flex items-center gap-2">
              <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => setPage(page - 1)} data-testid="page-prev"><ChevronLeft className="h-4 w-4" /></Button>
              <span>Page {page} of {pages}</span>
              <Button variant="outline" size="sm" disabled={page >= pages} onClick={() => setPage(page + 1)} data-testid="page-next"><ChevronRight className="h-4 w-4" /></Button>
            </div>
          </div>
        </div>
      )}
      <RecordPanel id={openId} onClose={() => setOpenId(null)} onSaved={load} />
    </div>
  );
}
